import { Controller } from "@hotwired/stimulus"
import { useTransition } from "stimulus-use"

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ")

export default class extends Controller {
  static targets = ["menu", "label"]
  static values = {
    selectable: Boolean,
  }

  connect() {
    useTransition(this, { element: this.menuTarget })

    this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this)
    this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this)
    this.handleMenuClick = this.handleMenuClick.bind(this)

    this.trigger = this.element.querySelector("[data-action*='railsui-dropdown#toggle']")
    this.triggerId = this.ensureElementId(this.trigger, "railsui-dropdown-trigger")
    this.menuId = this.ensureElementId(this.menuTarget, "railsui-dropdown-menu")

    if (this.trigger) {
      if (!this.isFieldDropdown()) {
        if (!this.trigger.hasAttribute("aria-haspopup")) {
          this.trigger.setAttribute("aria-haspopup", "menu")
        }
        this.trigger.setAttribute("aria-controls", this.menuId)
      }
      if (!this.trigger.hasAttribute("aria-expanded")) {
        this.trigger.setAttribute("aria-expanded", "false")
      }
      this.trigger.addEventListener("keydown", this.handleTriggerKeydown)
    }

    if (!this.isFieldDropdown()) {
      if (!this.menuTarget.hasAttribute("aria-labelledby") && this.triggerId) {
        this.menuTarget.setAttribute("aria-labelledby", this.triggerId)
      }

      if (!this.menuTarget.hasAttribute("role")) {
        this.menuTarget.setAttribute("role", "menu")
      }

      if (this.menuTarget.getAttribute("role") === "menu") {
        this.menuItems().forEach((item) => {
          if (!item.hasAttribute("role")) {
            item.setAttribute("role", "menuitem")
          }
        })
      }
    }

    this.setAriaState(this.isOpen)
    this.menuTarget.addEventListener("click", this.handleMenuClick)
    document.addEventListener("keydown", this.handleDocumentKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleDocumentKeydown)
    this.menuTarget.removeEventListener("click", this.handleMenuClick)

    if (this.trigger) {
      this.trigger.removeEventListener("keydown", this.handleTriggerKeydown)
    }
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (event.target.closest(".dropdown-field-clear-icon, [data-dropdown-clear]")) {
      const clearController = this.menuController()
      clearController?.clearField?.(event)
      return
    }

    const menuController = this.menuController()
    if (menuController) {
      if (event.type === "click" && event.detail === 0 && menuController.isOpen()) {
        return
      }

      menuController.toggle(event)
      return
    }

    if (event.type === "click" && event.detail === 0 && this.isOpen) {
      return
    }

    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  hide(event) {
    if (!this.element.contains(event.target)) {
      const menuController = this.menuController()
      if (menuController) {
        menuController.hide()
        return
      }

      this.close()
    }
  }

  open({ focus = null } = {}) {
    if (!this.isOpen) {
      if (this.toggleTransition) {
        this.toggleTransition()
      }

      this.setAriaState(true)
    }

    if (focus) {
      requestAnimationFrame(() => this.focusMenuItem(focus))
    }
  }

  close({ returnFocus = false } = {}) {
    if (!this.isOpen) {
      return
    }

    this.resetMenuFilters()

    if (this.leave) {
      this.leave()
    }

    this.setAriaState(false)

    if (returnFocus && this.trigger) {
      requestAnimationFrame(() => this.trigger.focus())
    }
  }

  handleTriggerKeydown(event) {
    if (this.usesMenuController()) {
      return
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.open({ focus: "first" })
        break
      case "ArrowUp":
        event.preventDefault()
        this.open({ focus: "last" })
        break
      case "Enter":
      case " ":
      case "Spacebar":
        event.preventDefault()
        this.open({ focus: "first" })
        break
    }
  }

  handleDocumentKeydown(event) {
    if (!this.isOpen) {
      return
    }

    if (this.usesMenuController() && event.key !== "Tab") {
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      this.close({ returnFocus: true })
      return
    }

    const activeElement = document.activeElement

    if (!this.menuTarget.contains(activeElement)) {
      return
    }

    const items = this.menuItems()

    if (!items.length) {
      return
    }

    const currentIndex = Math.max(items.indexOf(activeElement), 0)
    let nextIndex = currentIndex

    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        nextIndex = (currentIndex + 1) % items.length
        break
      case "ArrowUp":
      case "ArrowLeft":
        nextIndex = (currentIndex - 1 + items.length) % items.length
        break
      case "Home":
        nextIndex = 0
        break
      case "End":
        nextIndex = items.length - 1
        break
      case "Tab":
        requestAnimationFrame(() => {
          if (!this.element.contains(document.activeElement)) {
            this.close()
          }
        })
        return
      case " ":
        if (activeElement.tagName === "A") {
          event.preventDefault()
          activeElement.click()
        }
        return
      default:
        return
    }

    event.preventDefault()
    items[nextIndex].focus()
  }

  handleMenuClick(event) {
    if (event.target.closest(".form-search-clear, .dropdown-menu-create-row, .dropdown-menu-action")) {
      return
    }

    const item = event.target.closest(focusableSelector)

    if (!item) {
      return
    }

    if (this.selectableValue && item.dataset.railsuiDropdownOption === "true") {
      this.selectOption(item)
    }

    const menuController = this.menuController()
    if (menuController) {
      menuController.hide()
      this.trigger?.focus()
      return
    }

    this.close({ returnFocus: true })
  }

  menuController() {
    if (!this.application?.getControllerForElementAndIdentifier) {
      return null
    }

    return this.application.getControllerForElementAndIdentifier(this.element, "railsui-dropdown-menu")
  }

  usesMenuController() {
    return this.trigger?.dataset?.action?.includes("onTriggerKeydown") ?? false
  }

  isFieldDropdown() {
    return this.usesMenuController()
  }

  selectOption(selectedItem) {
    this.menuItems().forEach((item) => {
      const selected = item === selectedItem

      item.classList.toggle("is-selected", selected)

      if (item.getAttribute("role") === "option") {
        item.setAttribute("aria-selected", selected.toString())
      }
    })

    if (this.hasLabelTarget && selectedItem.dataset.label) {
      this.labelTarget.textContent = selectedItem.dataset.label
    }

    if (this.trigger && selectedItem.id) {
      this.trigger.setAttribute("aria-activedescendant", selectedItem.id)
    }
  }

  focusMenuItem(position) {
    const items = this.menuItems()

    if (!items.length) {
      return
    }

    ;(position === "last" ? items[items.length - 1] : items[0]).focus()
  }

  menuItems() {
    return Array.from(this.menuTarget.querySelectorAll(focusableSelector)).filter((item) => {
      if (item.hidden) {
        return false
      }

      if (item.getAttribute("aria-hidden") === "true") {
        return false
      }

      return !item.closest("[hidden]")
    })
  }

  filterList(event) {
    const query = event.target.value.trim().toLowerCase()
    this.menuTarget.querySelectorAll("[data-dropdown-filter-item]").forEach((row) => {
      const label = (row.dataset.dropdownFilterLabel || row.textContent || "").toLowerCase()
      row.hidden = query.length > 0 && !label.includes(query)
    })
  }

  resetMenuFilters() {
    this.menuTarget.querySelectorAll('input[type="text"].filter').forEach((input) => {
      input.value = ""
    })
    this.menuTarget.querySelectorAll("[data-dropdown-filter-item]").forEach((row) => {
      row.hidden = false
    })
  }

  ensureElementId(element, prefix) {
    if (!element) {
      return null
    }

    if (element.id) {
      return element.id
    }

    const generatedId = `${prefix}-${Math.random().toString(36).slice(2, 10)}`
    element.id = generatedId
    return generatedId
  }

  setAriaState(expanded) {
    if (this.trigger) {
      this.trigger.setAttribute("aria-expanded", expanded.toString())
    }

    this.menuTarget.setAttribute("aria-hidden", (!expanded).toString())
  }

  get isOpen() {
    return !this.menuTarget.classList.contains("hidden")
  }
}
